import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import NotificationList from '../../../components/notifications';
import { NotificationType } from '../../../models/general';
import { getNotifictionTypes } from '../../../api/General/Notification';
import { dismissAllNotifications } from '../../../store/redux/actions/notificationActions';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import AppIcon from '../../../components/AppIcon';

const Notifications: React.FC = () => {
  const { t } = useTranslation();
  const userId = useSelector((state: any) => state.user?.userId);
  const [selectedType, setSelectedType] = useState<string>('0');
  const [types, setTypes] = useState<NotificationType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [markAllVersion, setMarkAllVersion] = useState(0);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const dispatch = useDispatch();
  const [unreadCount, setUnreadCount] = useState(0);

  const loadTypes = async () => {
    setLoadingTypes(true);
    try {
      const { payload } = await getNotifictionTypes();
      setTypes(payload);
    } finally { setLoadingTypes(false); }
  };

  useEffect(() => { loadTypes(); }, []);

  const numericType = selectedType === '0' ? undefined : parseInt(selectedType);

  const handleDismissAll = async () => {
    if (!unreadCount || !userId) return;
    try {
      await (dispatch as any)(dismissAllNotifications(userId));
      setMarkAllVersion(v => v + 1);
    } catch (e) {
      console.error('Dismiss all failed', e);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-2 sm:p-3">
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-y-1">
          <div className="flex items-center gap-2">
            <AppIcon name="Filter" size={20} className="text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">{t('Notifications')}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              title={t('Reload types & notifications')}
              onClick={async () => { await loadTypes(); setRefreshVersion(v => v + 1); }}
              disabled={loadingTypes}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
              aria-label={t('Reload types')}
            >
              <AppIcon name="RefreshCw" size={16} />
            </button>
            <Button variant="ghost" size="sm" disabled={!unreadCount} onClick={handleDismissAll}>
              {t('Dismiss all')}
            </Button>
          </div>
        </div>
        <div className="h-px bg-border" />
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <Select
            label={t('Notification Type')}
            value={selectedType}
            options={[{ value: '0', label: t('All') }, ...types.map(tp => ({ value: tp.id.toString(), label: t(tp.name) }))]}
            onChange={(val: any) => setSelectedType(val)}
            className="min-w-60"
          />
          {numericType && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
              {t('Filtered')}
              <button onClick={() => setSelectedType('0')} className="ms-1 rounded-full hover:bg-primary/20 p-0.5">
                <AppIcon name="X" size={12} />
              </button>
            </span>
          )}
        </div>
      </div>
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
        <NotificationList
          type={numericType}
          full
          showHeader={false}
          markAllReadSignal={markAllVersion}
          onUnreadCountChange={setUnreadCount}
          refreshSignal={refreshVersion}
        />
      </div>
    </div>
  );
};

export default Notifications;
